import { KJUR } from "jsrsasign";

import { RecordingStatus } from "@zoom/videosdk";
import { ZOOM_SDK_KEY, ZOOM_SDK_SECRET } from "../constants/AppConstants";
import { ZoomClient, ZoomStream } from "../types/zoomClient.types";
import {
  errorLogger,
  EErrorLevel,
  EErrorSource,
  EInfoSource,
  infoLogger,
} from "./ubaTracking.utils";
import { getServerTime } from "./utils";

export function generateSignature(
  sdkKey: string,
  sdkSecret: string,
  sessionName: string,
  role: number,
  sessionKey: string,
  userIdentity: string,
  serverTimeOffsetTime: number,
) {
  let sdkJWT = "";
  try {
    const iat =
      Math.round(getServerTime(serverTimeOffsetTime).getTime() / 1000) - 30;
    const exp = iat + 60 * 60 * 2;
    const oHeader = { alg: "HS256", typ: "JWT" };

    const oPayload = {
      app_key: sdkKey,
      tpc: sessionName,
      role_type: role,
      session_key: sessionKey,
      user_identity: userIdentity,
      version: 1,
      iat,
      exp,
    };

    const oUbaPayload = {
      ...oPayload,
      app_key: undefined,
    };

    const sHeader = JSON.stringify(oHeader);
    const sPayload = JSON.stringify(oPayload);

    // UBA: infoLogger - zoom
    infoLogger("generating Signature for zoomsdk", EInfoSource.ZOOM, {
      ...oUbaPayload,
    });
    sdkJWT = KJUR.jws.JWS.sign("HS256", sHeader, sPayload, sdkSecret);
    // UBA: infoLogger - zoom
    infoLogger(
      "successfully generated Signature for zoomsdk",
      EInfoSource.ZOOM,
      {
        ...oUbaPayload,
        sdkJWT,
      },
    );
  } catch (error: unknown) {
    errorLogger(error as Error, EErrorLevel.HIGH, EErrorSource.ZOOM);
  }

  return sdkJWT;
}

export const joinClient = async (
  client: ZoomClient,
  name: string,
  userRole: number,
  interviewSlug: string,
  serverTimeOffsetTime: number,
) => {
  const zoomRecordingTitle = `interview_${interviewSlug.trim()}`;

  const username = name.trim();
  const password = undefined;
  const sessionKey = zoomRecordingTitle;
  const role = +userRole;
  const topic = zoomRecordingTitle;
  // UBA: infoLogger - zoom
  infoLogger("initializing zoom client", EInfoSource.ZOOM);

  await client.init("en-US", "CDN", {
    enforceMultipleVideos: true,
    stayAwake: true,
    patchJsMedia: true,
    leaveOnPageUnload: true,
  });

  // UBA: infoLogger - zoom
  infoLogger("zoom client intitalized successfully", EInfoSource.ZOOM);
  if (ZOOM_SDK_KEY && ZOOM_SDK_SECRET) {
    const signature = generateSignature(
      ZOOM_SDK_KEY,
      ZOOM_SDK_SECRET,
      topic,
      role,
      sessionKey,
      username,
      serverTimeOffsetTime,
    );
    // UBA: infoLogger - zoom
    infoLogger("initializing zoom join", EInfoSource.ZOOM);
    const data = await client.join(topic, signature, username, password);
    // UBA: infoLogger - zoom
    infoLogger("zoom join intitalized successfully", EInfoSource.ZOOM);
    return data;
  }
  return null;
};

export const handleStartVideo = async (
  selfVideoContainerRef: React.RefObject<HTMLVideoElement>,
  stream: ZoomStream | null,
  isVideoEnabled: boolean,
) => {
  if (
    stream &&
    stream.isRenderSelfViewWithVideoElement() &&
    selfVideoContainerRef?.current
  ) {
    // UBA: infoLogger - zoom
    infoLogger("initializing zoom video", EInfoSource.ZOOM);
    if (stream.isCapturingVideo()) await stream.stopVideo();
    if (isVideoEnabled)
      await stream.startVideo({
        videoElement: selfVideoContainerRef.current,
      });
  }
};

export const handleStartAudio = async (
  stream: ZoomStream | null,
  client: ZoomClient | null,
  audioEncode: boolean | undefined,
  audioDecode: boolean | undefined,
  isAudioEnabled: boolean,
) => {
  if (client && stream && !stream.getShareAudioStatus().isShareAudioEnabled) {
    // UBA: infoLogger - zoom
    infoLogger("initializing zoom audio", EInfoSource.ZOOM);
    const isSafari = navigator.userAgent.search("safari") !== -1;
    if (isSafari) {
      if (audioEncode && audioDecode) {
        await stream.startAudio({
          mute: !isAudioEnabled,
          highBitrate: true,
          // syncButtonsOnHeadset: true,
        });
      } else {
        // eslint-disable-next-line no-console
        console.log("safari audio has not finished initializing");
        // UBA: infoLogger - zoom
        infoLogger(
          "safari audio has not finished initializing",
          EInfoSource.ZOOM,
        );
      }
    } else {
      await stream.startAudio({
        mute: !isAudioEnabled,
        highBitrate: true,
        // syncButtonsOnHeadset: true,
      });
    }
  }
};

export const handleRemoveAudioVideo = async (
  stream: ZoomStream | null,
  client: ZoomClient | null,
) => {
  try {
    if (client?.getSessionInfo().isInMeeting) {
      // UBA: infoLogger - zoom
      infoLogger("removing zoom audio and video stream", EInfoSource.ZOOM);
      if (client?.getCurrentUserInfo()?.audio !== "") {
        await stream?.stopAudio();
      } else {
        errorLogger(
          new Error(
            "error in stopping the audio via the handleRemoveAudioVideo function",
          ),
          EErrorLevel.HIGH,
          EErrorSource.ZOOM,
        );
      }
      if (client?.getCurrentUserInfo()?.bVideoOn) {
        await stream?.stopVideo();
      } else {
        errorLogger(
          new Error(
            "error in stopping the video via the handleRemoveAudioVideo function",
          ),
          EErrorLevel.HIGH,
          EErrorSource.ZOOM,
        );
      }
    }
  } catch (error: unknown) {
    errorLogger(error as Error, EErrorLevel.HIGH, EErrorSource.ZOOM);
  }
};

export const stopScreenShare = async (
  client: ZoomClient | null,
  stream: ZoomStream | null,
) => {
  try {
    if (client?.getSessionInfo().isInMeeting) {
      // UBA: infoLogger - zoom
      infoLogger("stopping zoom screen share", EInfoSource.ZOOM);
      await stream?.stopShareScreen();
    }
  } catch (error: unknown) {
    errorLogger(error as Error, EErrorLevel.HIGH, EErrorSource.ZOOM);
  }
};

export const leaveUserFromCall = async (
  client: ZoomClient | null,
  end?: boolean,
) => {
  try {
    if (client?.getSessionInfo().isInMeeting) {
      // UBA: infoLogger - zoom
      infoLogger("leaving zoom call", EInfoSource.ZOOM);
      await client?.leave(end);
    }
  } catch (error: unknown) {
    errorLogger(error as Error, EErrorLevel.HIGH, EErrorSource.ZOOM);
  }
};

export const handleCloudRecording = (
  client: ZoomClient | null,
  stream: ZoomStream | null,
) => {
  if (client && stream) {
    const cloudRecording = client.getRecordingClient();
    const recordingStatus = cloudRecording.getCloudRecordingStatus();
    if (recordingStatus === RecordingStatus.Stopped) {
      infoLogger("initializing zoom cloud recording", EInfoSource.ZOOM);
      cloudRecording
        .startCloudRecording()
        .then(() => {
          infoLogger(
            "zoom cloud recording started successfully",
            EInfoSource.ZOOM,
          );
        })
        .catch((error: Error) => {
          errorLogger(error, EErrorLevel.HIGH, EErrorSource.ZOOM);
        });
    }
  }
};

export const handleStopCloudRecording = (
  client: ZoomClient | null,
  stream: ZoomStream | null,
) => {
  infoLogger("stopping zoom cloud recording", EInfoSource.ZOOM);
  if (client && stream) {
    const cloudRecording = client.getRecordingClient();
    const recordingStatus = cloudRecording.getCloudRecordingStatus();
    if (recordingStatus === RecordingStatus.Recording) {
      cloudRecording
        .stopCloudRecording()
        .then(() => {
          infoLogger(
            "zoom cloud recording stopped successfully",
            EInfoSource.ZOOM,
          );
        })
        .catch((error: Error) => {
          errorLogger(error, EErrorLevel.HIGH, EErrorSource.ZOOM);
        });
    }
  }
};
